import { Route, Routes } from "react-router-dom";

import ACL_RELATIONSHIPS from "acl-relationships";
import Authorized from "components/Authorized";
import ForbiddenRoute from "components/ForbiddenRoute";
import { useRuvixxFlags } from "providers/RuvixxFlagsProvider";
import CONFIG from "Config";
import NotFoundItem from "routes/404/NotFoundItem";
import AgentDashboardTable from "routes/agent_dashboard/AgentDashboardTableV3";
import CallsTable from "components/CallsTableV3/CallsTableV3";
import CampaignDetail from "routes/campaign/CampaignDetail";
import CampaignsTable from "routes/campaign/CampaignsTableV3";
import CampaignTarget from "routes/campaign/CampaignTarget";
import CampaignFormPage from "routes/campaign/form/CampaignFormPage";
import CaseDetail from "routes/case/CaseDetail";
import CaseMap from "components/CaseMap";
import CasesTable from "routes/case/CasesTableV3";
import CaseFormPage from "routes/case/form/CaseFormPage";
import Contact from "routes/contact/Contact";
import ContactsTable from "routes/contact/components/contacts_table/ContactsTableV3";
import ContactFormPage from "routes/contact/form/ContactFormPage";
import CallDashboard from "routes/dashboard/CallDashboard";
import DataJobTable from "routes/data_tab/DataJobTable";
import DialSessionsTable from "routes/dial_sessions/components/DialSessionTable/DialSessionTableV3";
import PowerDialer from "routes/dial_sessions/PowerDialer";
import PowerDialerMonitor from "routes/dial_sessions/PowerDialerMonitor";
import EmailFooter from "routes/email/EmailFooter";
import EmailSignature from "routes/email/EmailSignature";
import EmailExemption from "routes/email/EmailExemption";
import EmailAttachment from "routes/email/EmailAttachment";
import EmailTemplate from "routes/email/EmailTemplate";
import Entity from "routes/entity/Entity";
import EntitiesTable from "routes/entity/components/entity_table/EntitiesTableV3";
import EntityFormPage from "routes/entity/form/EntityFormPage";
import SwoogoConnectionPage from "routes/swoogo/ConnectionPage";
import SwoogoAccountPage from "routes/swoogo/SwoogoAccountPage";
import SwoogoEventDetail from "routes/swoogo/EventDetail";
import SwoogoRegistrantDetail from "routes/swoogo/RegistrantDetail";
import SwoogoSessionDetail from "routes/swoogo/SessionDetail";

import Login from "routes/login/Login";
import OAuthLogin from "routes/oauth-login/OAuthLogin";
import ResetPasswordRedirect from "routes/login/ResetPasswordRedirect";
import UiSandboxRouter from "routes/ui_sandbox/UiSandboxRouter";
import SmtpAccountsTable from "routes/smtp_accounts/SmtpAccountsTableV3";
import UserSettings from "routes/user/User";
import Settings from "routes/settings/Settings";
import RevenueOpportunitiesTable from "components/RevenueOpportunitiesTableV3/RevenueOpportunitiesTableV3";
import RevenueOpportunity from "routes/revenue_opportunity/RevenueOpportunity";
import RevenueOpportunityFormPage from "routes/revenue_opportunity/form/RevenueOpportunityFormPage";
import MachinesTable from "routes/machine/MachinesTableV3";
import { COMPLETE_MACHINES_TABLE_NAME } from "routes/machine/MachinesTableConstants";
import TableDataPage from "routes/custom_tables/TableDataPage";
import {
  LeadSourceForm,
  LeadSourceLeadDetail,
  LeadSourceLeads,
  LeadSourceManage,
} from "routes/leads/LeadSourceRouter";
import CustomTableRowForm from "routes/custom_tables/CustomTableRowForm";
import CustomTableRowDetail from "routes/custom_tables/CustomTableRowDetail";
import ImportWizard from "routes/custom_tables/import/ImportWizard";
import { CollapsedSidebarLayout } from "components/layouts/CollapsedSidebarLayout";
import { OpenLayout } from "components/layouts/OpenLayout";
import { PaddedLayout } from "components/layouts/PaddedLayout";
import { ProtectedLayout } from "components/layouts/ProtectedLayout";
import SchemaEditor from "routes/custom_tables/settings/SchemaEditor";
import CustomTablesAdmin from "routes/custom_tables/settings/CustomTablesAdmin";
import TagForm from "routes/tags/TagForm";
import NoteTemplateForm from "routes/note_template/NoteTemplateForm";
import EmailUrl from "routes/email_urls/EmailUrl";
import UserForm from "routes/admin/components/UserForm";
import RoleForm from "routes/admin/components/RoleForm";
import FeatureFlagsPage from "routes/feature_flags/FeatureFlagsPage";
import FeatureFlagForm from "routes/feature_flags/FeatureFlagForm";
import TagManagementTable from "routes/tags/TagManagementTableV3";
import SettingsLayout from "routes/settings/SettingsLayout";
import CustomFieldConfig from "routes/custom_field/CustomFieldConfig";
import MetaDatasetsPage from "routes/meta_datasets/MetaDatasetsPage";
import MetaDatasetForm from "routes/meta_datasets/MetaDatasetForm";

// Module-level so each list keeps one identity across renders; ForbiddenRoute
// records the denied grants only when that list's outcome changes.
const CUSTOM_FIELD_CONFIG_PERMISSIONS = [
  ACL_RELATIONSHIPS.customFieldConfig.readTable,
];

const CAMPAIGNS_TABLE_PERMISSIONS = [
  ACL_RELATIONSHIPS.campaign.read,
  ACL_RELATIONSHIPS.campaigns.read,
  ACL_RELATIONSHIPS.region.read,
];

const CAMPAIGN_DETAIL_PERMISSIONS = [
  ACL_RELATIONSHIPS.campaign.read,
  ACL_RELATIONSHIPS.campaigns.read,
  ACL_RELATIONSHIPS.region.read,
  ACL_RELATIONSHIPS.tag.read,
  ACL_RELATIONSHIPS.campaignActivity.read,
  ACL_RELATIONSHIPS.user.read,
  ACL_RELATIONSHIPS.emailTemplates.read,
  ACL_RELATIONSHIPS.campaignTarget.read,
];

const CASE_PERMISSIONS = [ACL_RELATIONSHIPS.case.read];

const CASE_MAP_PERMISSIONS = [
  ACL_RELATIONSHIPS.case.read,
  ACL_RELATIONSHIPS.machine.read,
];

const EMAIL_TEMPLATE_PERMISSIONS = [ACL_RELATIONSHIPS.emailTemplates.read];

const EMAIL_FOOTER_PERMISSIONS = [ACL_RELATIONSHIPS.emailFooters.read];

const EMAIL_SIGNATURE_PERMISSIONS = [ACL_RELATIONSHIPS.emailSignatures.read];

const EMAIL_EXEMPTION_PERMISSIONS = [ACL_RELATIONSHIPS.emailExemption.read];

const EMAIL_ATTACHMENT_PERMISSIONS = [ACL_RELATIONSHIPS.emailAttachments.read];

const ENTITIES_TABLE_PERMISSIONS = [
  ACL_RELATIONSHIPS.user.read,
  ACL_RELATIONSHIPS.tag.read,
  ACL_RELATIONSHIPS.entityCustomFields.read,
  ACL_RELATIONSHIPS.entity.read,
];

const ENTITY_PERMISSIONS = [
  ACL_RELATIONSHIPS.entity.read,
  ACL_RELATIONSHIPS.tag.read,
  ACL_RELATIONSHIPS.entityCampaigns.read,
  ACL_RELATIONSHIPS.campaigns.read,
  ACL_RELATIONSHIPS.auditLog.read,
  ACL_RELATIONSHIPS.user.read,
  ACL_RELATIONSHIPS.twilioToken.read,
  ACL_RELATIONSHIPS.disposition.read,
];

const USER_SETTINGS_PERMISSIONS = [ACL_RELATIONSHIPS.userSettings.read];

const SETTINGS_PERMISSIONS = [
  ACL_RELATIONSHIPS.adminUser.read,
  ACL_RELATIONSHIPS.adminRole.read,
  ACL_RELATIONSHIPS.adminGroup.create,
  ACL_RELATIONSHIPS.disposition.read,
  ACL_RELATIONSHIPS.voicemail.read,
  ACL_RELATIONSHIPS.campaignStatusPreset.readAsTable,
  ACL_RELATIONSHIPS.region.read,
  ACL_RELATIONSHIPS.revenueOpportunityStatus.read,
  ACL_RELATIONSHIPS.constantContactAccounts.read,
  ACL_RELATIONSHIPS.constantContactCampaigns.read,
  ACL_RELATIONSHIPS.customFieldGroup.read,
  ACL_RELATIONSHIPS.customFieldConfig.readTable,
  ACL_RELATIONSHIPS.customTable.readSchema,
  ACL_RELATIONSHIPS.product.read,
  ACL_RELATIONSHIPS.tag.readTable,
  ACL_RELATIONSHIPS.tagCategory.readTable,
];

const CONTACTS_TABLE_PERMISSIONS = [
  ACL_RELATIONSHIPS.user.read,
  ACL_RELATIONSHIPS.tag.read,
  ACL_RELATIONSHIPS.contactCustomFields.read,
  ACL_RELATIONSHIPS.contact.read,
];

const CONTACT_PERMISSIONS = [
  ACL_RELATIONSHIPS.contact.read,
  ACL_RELATIONSHIPS.contactCampaigns.read,
  ACL_RELATIONSHIPS.tag.read,
  ACL_RELATIONSHIPS.entity.read,
  ACL_RELATIONSHIPS.campaigns.read,
  ACL_RELATIONSHIPS.auditLog.read,
  ACL_RELATIONSHIPS.user.read,
  ACL_RELATIONSHIPS.twilioToken.read,
  ACL_RELATIONSHIPS.disposition.read,
];

const DIAL_SESSIONS_PERMISSIONS = [ACL_RELATIONSHIPS.dialSession.read];

const CALLS_TABLE_PERMISSIONS = [
  ACL_RELATIONSHIPS.campaignTargetCall.readTable,
];

const POWER_DIALER_PERMISSIONS = [
  ACL_RELATIONSHIPS.dialSession.read,
  ACL_RELATIONSHIPS.callQueueNextContact.read,
  ACL_RELATIONSHIPS.callQueueAddContact.create,
  ACL_RELATIONSHIPS.disposition.read,
  ACL_RELATIONSHIPS.emailUrlForms.read,
  ACL_RELATIONSHIPS.twilioToken.read,
  ACL_RELATIONSHIPS.tag.read,
  ACL_RELATIONSHIPS.user.read,
  ACL_RELATIONSHIPS.campaignStatuses.read,
  ACL_RELATIONSHIPS.twilioDisposition.create,
  ACL_RELATIONSHIPS.twilioAddToCall.create,
  ACL_RELATIONSHIPS.twilioRemoveFromCall.create,
  ACL_RELATIONSHIPS.twilioUpdateParticipant.create,
];

const POWER_DIALER_MONITOR_PERMISSIONS = [
  ACL_RELATIONSHIPS.dialSession.read,
  ACL_RELATIONSHIPS.callQueueNextContact.read,
  ACL_RELATIONSHIPS.callQueueAddContact.create,
  ACL_RELATIONSHIPS.disposition.read,
  ACL_RELATIONSHIPS.emailUrlForms.read,
  ACL_RELATIONSHIPS.twilioToken.read,
  ACL_RELATIONSHIPS.tag.read,
  ACL_RELATIONSHIPS.user.read,
  ACL_RELATIONSHIPS.campaignStatuses.read,
  ACL_RELATIONSHIPS.callQueueItemsWithAgentAssigned.read,
  ACL_RELATIONSHIPS.twilioDisposition.create,
  ACL_RELATIONSHIPS.twilioAddToCall.create,
  ACL_RELATIONSHIPS.twilioRemoveFromCall.create,
  ACL_RELATIONSHIPS.twilioUpdateParticipant.create,
];

const AGENT_DASHBOARD_PERMISSIONS = [
  ACL_RELATIONSHIPS.dialSession.read,
  ACL_RELATIONSHIPS.callQueueNextContact.read,
  ACL_RELATIONSHIPS.disposition.read,
  ACL_RELATIONSHIPS.emailUrlForms.read,
  ACL_RELATIONSHIPS.twilioToken.read,
  ACL_RELATIONSHIPS.tag.read,
  ACL_RELATIONSHIPS.user.read,
  ACL_RELATIONSHIPS.campaignStatuses.read,
  ACL_RELATIONSHIPS.callQueueItemsWithAgentAssigned.read,
  ACL_RELATIONSHIPS.twilioDisposition.create,
  ACL_RELATIONSHIPS.twilioAddToCall.create,
  ACL_RELATIONSHIPS.twilioRemoveFromCall.create,
  ACL_RELATIONSHIPS.twilioUpdateParticipant.create,
];

const CALL_DASHBOARD_PERMISSIONS = [
  ACL_RELATIONSHIPS.campaigns.read,
  ACL_RELATIONSHIPS.dialSession.read,
  ACL_RELATIONSHIPS.disposition.read,
  ACL_RELATIONSHIPS.user.read,
  ACL_RELATIONSHIPS.campaignTargetCall.read,
];

const SMTP_ACCOUNTS_PERMISSIONS = [
  ACL_RELATIONSHIPS.emailConfigSmtp.read,
  ACL_RELATIONSHIPS.emailConfigSmtpTable.read,
];

const DATA_JOB_PERMISSIONS = [ACL_RELATIONSHIPS.dataJob.read];

const REVENUE_OPPORTUNITIES_TABLE_PERMISSIONS = [
  ACL_RELATIONSHIPS.revenueOpportunity.readTable,
];

const MACHINES_TABLE_PERMISSIONS = [ACL_RELATIONSHIPS.machine.read];

const SCHEMA_EDITOR_PERMISSIONS = [ACL_RELATIONSHIPS.customTable.editSchema];

const CUSTOM_TABLES_ADMIN_PERMISSIONS = [
  ACL_RELATIONSHIPS.customTable.readSchema,
];

const TAG_MANAGEMENT_PERMISSIONS = [ACL_RELATIONSHIPS.tag.readTable];

// SECURITY: a UI gate only. The API enforces the same grants on every request.
const RouteGuard = ({ permissions, partialMatch = false, children }) => (
  <Authorized
    permissions={permissions}
    partialMatch={partialMatch}
    fallback={
      <ForbiddenRoute permissions={permissions} partialMatch={partialMatch} />
    }
  >
    {children}
  </Authorized>
);

const Router = () => {
  const {
    enable_ruvixx_flags_sys_settings,
    enable_leads_feature,
    enable_meta_account,
    enable_swoogo_integration,
    enable_ui_sandbox,
  } = useRuvixxFlags();
  const isMetaAccountEnabled = enable_leads_feature && enable_meta_account;

  const campaignsTable = (
    <RouteGuard permissions={CAMPAIGNS_TABLE_PERMISSIONS}>
      <CampaignsTable />
    </RouteGuard>
  );

  const settings = (
    <RouteGuard permissions={SETTINGS_PERMISSIONS} partialMatch>
      <Settings />
    </RouteGuard>
  );

  return (
    <Routes>
      <Route element={<OpenLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/oauth-login" element={<OAuthLogin />} />
        <Route path="/reset_password" element={<ResetPasswordRedirect />} />
      </Route>

      <Route element={<ProtectedLayout />}>
        <Route element={<PaddedLayout />}>
          <Route path="/" element={campaignsTable} />
          <Route
            path="agent_dashboard"
            element={
              <RouteGuard permissions={AGENT_DASHBOARD_PERMISSIONS}>
                <AgentDashboardTable />
              </RouteGuard>
            }
          />
          <Route
            path="calls"
            element={
              <RouteGuard permissions={CALLS_TABLE_PERMISSIONS}>
                <CallsTable />
              </RouteGuard>
            }
          />
          {CONFIG.IS_PHONE_ENABLED ? (
            <Route
              path="dial_sessions"
              element={
                <RouteGuard permissions={DIAL_SESSIONS_PERMISSIONS}>
                  <DialSessionsTable />
                </RouteGuard>
              }
            />
          ) : null}
          <Route path="campaigns" element={campaignsTable} />
          <Route
            path="cases"
            element={
              <RouteGuard permissions={CASE_PERMISSIONS}>
                <CasesTable />
              </RouteGuard>
            }
          />
          <Route
            path="contacts"
            element={
              <RouteGuard permissions={CONTACTS_TABLE_PERMISSIONS}>
                <ContactsTable />
              </RouteGuard>
            }
          />
          <Route path="custom_tables/import" element={<ImportWizard />} />
          <Route
            path="custom_tables/:slug/new"
            element={<CustomTableRowForm mode="create" />}
          />
          <Route
            path="custom_tables/:slug/:rowId/edit"
            element={<CustomTableRowForm mode="edit" />}
          />
          <Route path="custom_tables/:slug" element={<TableDataPage />} />
          <Route
            path="entities"
            element={
              <RouteGuard permissions={ENTITIES_TABLE_PERMISSIONS}>
                <EntitiesTable />
              </RouteGuard>
            }
          />
          <Route
            path="machines"
            element={
              <RouteGuard permissions={MACHINES_TABLE_PERMISSIONS}>
                <MachinesTable tableName={COMPLETE_MACHINES_TABLE_NAME} />
              </RouteGuard>
            }
          />
          <Route
            path="revenue_opportunities"
            element={
              <RouteGuard permissions={REVENUE_OPPORTUNITIES_TABLE_PERMISSIONS}>
                <RevenueOpportunitiesTable />
              </RouteGuard>
            }
          />
          <Route
            path="profile"
            element={
              <RouteGuard permissions={USER_SETTINGS_PERMISSIONS}>
                <UserSettings />
              </RouteGuard>
            }
          />
          <Route
            path="smtp_accounts"
            element={
              <RouteGuard permissions={SMTP_ACCOUNTS_PERMISSIONS}>
                <SmtpAccountsTable />
              </RouteGuard>
            }
          />
          <Route
            path="call_dashboard/*"
            element={
              <RouteGuard permissions={CALL_DASHBOARD_PERMISSIONS}>
                <CallDashboard />
              </RouteGuard>
            }
          />
          {enable_swoogo_integration && (
            <Route path="swoogo/*" element={<SwoogoAccountPage />} />
          )}
          <Route path="*" element={<NotFoundItem />} />
        </Route>

        <Route element={<CollapsedSidebarLayout />}>
          {/* Record routes end in a splat: the first segment after the record
              is the tab, and deeper segments are left for that tab's own
              sub-routes. A static sibling such as cases/:id/map always outranks
              the splat, so it is never read as a tab. */}

          {/* Under the record page's layout, so opening a form keeps the
              global rail as the record page left it. Each form lays out its
              own header bar and scrolling body. */}
          <Route
            path="revenue_opportunities/new"
            element={<RevenueOpportunityFormPage />}
          />
          <Route
            path="revenue_opportunities/:id/edit"
            element={<RevenueOpportunityFormPage />}
          />
          <Route path="contacts/new" element={<ContactFormPage />} />
          <Route path="contacts/:id/edit" element={<ContactFormPage />} />
          <Route path="entities/new" element={<EntityFormPage />} />
          <Route path="entities/:id/edit" element={<EntityFormPage />} />
          <Route path="cases/:id/edit" element={<CaseFormPage />} />
          <Route path="campaigns/new" element={<CampaignFormPage />} />
          <Route path="campaigns/:id/edit" element={<CampaignFormPage />} />
          <Route
            path="data"
            element={
              <RouteGuard permissions={DATA_JOB_PERMISSIONS}>
                <DataJobTable />
              </RouteGuard>
            }
          />
          {enable_swoogo_integration && (
            <>
              <Route
                path="swoogo/events/:id/*"
                element={<SwoogoEventDetail />}
              />
              <Route
                path="swoogo/registrants/:id/*"
                element={<SwoogoRegistrantDetail />}
              />
              <Route
                path="swoogo/sessions/:id/*"
                element={<SwoogoSessionDetail />}
              />
            </>
          )}

          <Route
            path="cases/:id/map"
            element={
              <RouteGuard permissions={CASE_MAP_PERMISSIONS}>
                <CaseMap />
              </RouteGuard>
            }
          />
          {isMetaAccountEnabled && (
            <Route
              path="leads/:service/:accountId/lead/:leadId/*"
              element={<LeadSourceLeadDetail />}
            />
          )}
          {isMetaAccountEnabled && (
            <Route
              path="leads/:service/:accountId/*"
              element={<LeadSourceLeads />}
            />
          )}
          <Route
            path="cases/:id/*"
            element={
              <RouteGuard permissions={CASE_PERMISSIONS}>
                <CaseDetail />
              </RouteGuard>
            }
          />
          <Route
            path="contacts/:id/*"
            element={
              <RouteGuard permissions={CONTACT_PERMISSIONS}>
                <Contact />
              </RouteGuard>
            }
          />
          <Route
            path="entities/:id/*"
            element={
              <RouteGuard permissions={ENTITY_PERMISSIONS}>
                <Entity />
              </RouteGuard>
            }
          />
          <Route
            path="revenue_opportunities/:id/*"
            element={<RevenueOpportunity />}
          />

          <Route path="campaigns">
            <Route
              path=":id/*"
              element={
                <RouteGuard permissions={CAMPAIGN_DETAIL_PERMISSIONS}>
                  <CampaignDetail />
                </RouteGuard>
              }
            />
            <Route
              path=":id/target/:entity_id/*"
              element={<CampaignTarget />}
            />
          </Route>

          <Route
            path="custom_tables/:slug/:rowId/*"
            element={<CustomTableRowDetail />}
          />

          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={settings} />
            <Route
              path="tags"
              element={
                <RouteGuard permissions={TAG_MANAGEMENT_PERMISSIONS}>
                  <TagManagementTable />
                </RouteGuard>
              }
            />
            <Route path="tags/new" element={<TagForm mode="create" />} />
            <Route path="tags/:id/edit" element={<TagForm mode="edit" />} />
            {enable_ruvixx_flags_sys_settings && (
              <>
                <Route path="feature_flags" element={<FeatureFlagsPage />} />
                <Route
                  path="feature_flags/new"
                  element={<FeatureFlagForm mode="create" />}
                />
                <Route
                  path="feature_flags/:id/edit"
                  element={<FeatureFlagForm mode="edit" />}
                />
              </>
            )}
            <Route path="users/new" element={<UserForm mode="create" />} />
            <Route path="users/:id/edit" element={<UserForm mode="edit" />} />
            <Route path="roles/new" element={<RoleForm mode="create" />} />
            <Route path="roles/:id/edit" element={<RoleForm mode="edit" />} />
            <Route
              path="note_templates/new"
              element={<NoteTemplateForm mode="create" />}
            />
            <Route
              path="note_templates/:id/edit"
              element={<NoteTemplateForm mode="edit" />}
            />
            <Route
              path="custom_tables"
              element={
                <RouteGuard permissions={CUSTOM_TABLES_ADMIN_PERMISSIONS}>
                  <CustomTablesAdmin />
                </RouteGuard>
              }
            />
            <Route
              path="custom_tables/new"
              element={
                <RouteGuard permissions={SCHEMA_EDITOR_PERMISSIONS}>
                  <SchemaEditor />
                </RouteGuard>
              }
            />
            <Route
              path="custom_tables/:slug/edit"
              element={
                <RouteGuard permissions={SCHEMA_EDITOR_PERMISSIONS}>
                  <SchemaEditor />
                </RouteGuard>
              }
            />
            {enable_swoogo_integration && (
              <Route
                path="integrations/swoogo"
                element={<SwoogoConnectionPage />}
              />
            )}
            {isMetaAccountEnabled && (
              <>
                <Route
                  path="integrations/:service"
                  element={<LeadSourceManage />}
                />
                <Route
                  path="integrations/:service/new"
                  element={<LeadSourceForm mode="create" />}
                />
                <Route
                  path="integrations/:service/:id/edit"
                  element={<LeadSourceForm mode="edit" />}
                />
              </>
            )}
            {enable_leads_feature && (
              <>
                <Route path="meta_datasets" element={<MetaDatasetsPage />} />
                <Route
                  path="meta_datasets/new"
                  element={<MetaDatasetForm mode="create" />}
                />
                <Route
                  path="meta_datasets/:id/edit"
                  element={<MetaDatasetForm mode="edit" />}
                />
              </>
            )}
            <Route
              path="email_templates/new"
              element={
                <RouteGuard permissions={EMAIL_TEMPLATE_PERMISSIONS}>
                  <EmailTemplate />
                </RouteGuard>
              }
            />
            <Route
              path="email_templates/:id"
              element={
                <RouteGuard permissions={EMAIL_TEMPLATE_PERMISSIONS}>
                  <EmailTemplate mode="edit" />
                </RouteGuard>
              }
            />
            <Route path="email_urls/new" element={<EmailUrl mode="create" />} />
            <Route
              path="email_urls/:id/edit"
              element={<EmailUrl mode="edit" />}
            />
            <Route
              path="email_footers/new"
              element={
                <RouteGuard permissions={EMAIL_FOOTER_PERMISSIONS}>
                  <EmailFooter />
                </RouteGuard>
              }
            />
            <Route
              path="email_footers/:id"
              element={
                <RouteGuard permissions={EMAIL_FOOTER_PERMISSIONS}>
                  <EmailFooter mode="edit" />
                </RouteGuard>
              }
            />
            <Route
              path="email_signatures/new"
              element={
                <RouteGuard permissions={EMAIL_SIGNATURE_PERMISSIONS}>
                  <EmailSignature />
                </RouteGuard>
              }
            />
            <Route
              path="email_signatures/:id"
              element={
                <RouteGuard permissions={EMAIL_SIGNATURE_PERMISSIONS}>
                  <EmailSignature mode="edit" />
                </RouteGuard>
              }
            />
            <Route
              path="email_exemptions/new"
              element={
                <RouteGuard permissions={EMAIL_EXEMPTION_PERMISSIONS}>
                  <EmailExemption />
                </RouteGuard>
              }
            />
            <Route
              path="email_exemptions/:id"
              element={
                <RouteGuard permissions={EMAIL_EXEMPTION_PERMISSIONS}>
                  <EmailExemption mode="edit" />
                </RouteGuard>
              }
            />
            <Route
              path="email_attachments/new"
              element={
                <RouteGuard permissions={EMAIL_ATTACHMENT_PERMISSIONS}>
                  <EmailAttachment />
                </RouteGuard>
              }
            />
            <Route
              path="email_attachments/:id"
              element={
                <RouteGuard permissions={EMAIL_ATTACHMENT_PERMISSIONS}>
                  <EmailAttachment mode="edit" />
                </RouteGuard>
              }
            />
            <Route
              path="custom_field_configs/new"
              element={
                <RouteGuard permissions={CUSTOM_FIELD_CONFIG_PERMISSIONS}>
                  <CustomFieldConfig />
                </RouteGuard>
              }
            />
            <Route
              path="custom_field_configs/:id"
              element={
                <RouteGuard permissions={CUSTOM_FIELD_CONFIG_PERMISSIONS}>
                  <CustomFieldConfig mode="edit" />
                </RouteGuard>
              }
            />
            <Route path=":item/*" element={settings} />
          </Route>

          {enable_ui_sandbox && (
            <Route path="ui-sandbox/*" element={<UiSandboxRouter />} />
          )}
        </Route>
      </Route>

      <Route element={<ProtectedLayout hideNav />}>
        <Route
          path="dialer/:id"
          element={
            <RouteGuard permissions={POWER_DIALER_PERMISSIONS}>
              <PowerDialer />
            </RouteGuard>
          }
        />
        <Route
          path="dialer-monitor/:campaignId/:callId"
          element={
            <RouteGuard permissions={POWER_DIALER_MONITOR_PERMISSIONS}>
              <PowerDialerMonitor />
            </RouteGuard>
          }
        />
      </Route>
    </Routes>
  );
};

export { Router };
